import { useQuery } from "@apollo/client";
import { compact } from "lodash";
import {
  Route,
  Switch,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { GetProfileQueryQueryVariables } from "types/graphql-schema";
import { GetProfileQueryQuery } from "types/graphql-schema";
import { TFLocationState } from "types/topicflow";

import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import {
  ToggleButtonGroup,
  ToggleButtonGroupType,
} from "@components/toggle-button-group/toggle-button-group";
import { ToggleButtonGroupTheme } from "@components/toggle-button-group/toggle-button-group-constants";
import { assertEdgesNonNull, getUrlUsingTypename } from "@helpers/helpers";

import ProfileCareer from "./career-tab/profile-career-tab";
import CoachingPage from "./coaching-tab/profile-coaching-tab";
import getProfileQuery from "./graphql/get-profile-query";
import ProfileEmptyState from "./profile-empty-state";
import ProfileOverview from "./profile-overview";
import ProfilePageSidebar from "./profile-page-sidebar";
import ProfileWorkHistory from "./profile-work-history";

const ProfilePage = () => {
  const { path } = useRouteMatch();
  const link = useLink();
  const location = useLocation<TFLocationState>();
  const { profileId: profileIdStr } = useParams<{
    profileId: string | undefined;
  }>();
  const currentUser = currentUserVar();
  const organization = currentOrganizationVar();
  const profileId = profileIdStr ? parseInt(profileIdStr) : undefined;

  const { data, loading } = useQuery<
    GetProfileQueryQuery,
    GetProfileQueryQueryVariables
  >(getProfileQuery, {
    variables: { userId: profileId || -1 },
    skip: !profileId,
  });
  const profile = data?.user;
  const isManager = profile?.managersList.some(
    ({ id }) => id === currentUser.id,
  );

  const isAdmin = isAdminVar();
  const canViewCareerTab =
    profile &&
    (isAdmin ||
      profile.isInCurrentUserReportingTree ||
      currentUser.id === profile.id);

  const directReports = assertEdgesNonNull(currentUser.directReports);
  const currentUserIsManagerOfProfile = directReports.find(
    ({ id }) => id === profileId,
  );
  const showPrivateNotes =
    (currentUser.id === profile?.id || currentUserIsManagerOfProfile) &&
    waffle.flag_is_active("private-notes");
  const showCoachingTab =
    !!currentUserIsManagerOfProfile &&
    waffle.flag_is_active("coaching-insights");
  const coachingManagerId: number = currentUser.id;

  const showTabs = profileId === currentUser.id || isManager;

  const tabButtons = compact([
    showTabs && {
      value: "overview",
      title: "Overview",
      active: location.pathname === `/profile/${profileId}`,
    },
    showTabs &&
      organization.featureFlags.workHistory && {
        value: "work-history",
        title: "Work History",
        active: location.pathname.includes(
          `/profile/${profileId}/work-history`,
        ),
      },
    showTabs &&
      showCoachingTab && {
        value: "coaching",
        title: "Coaching Insights",
        active: location.pathname.includes(`/profile/${profileId}/coaching`),
      },
    showTabs &&
      canViewCareerTab && {
        value: "career",
        title: "Career",
        active: location.pathname.includes(`/profile/${profileId}/career`),
      },
  ]);

  const handleTabClick = (button: ToggleButtonGroupType<string>) => {
    if (button.value === "overview") {
      link.redirect(`/profile/${profileId}`);
    } else {
      link.redirect(`/profile/${profileId}/${button.value}`);
    }
  };

  const isCoachingTab = location.pathname.includes(
    `/profile/${profileId}/coaching`,
  );

  return (
    <Layout className={isCoachingTab ? "!bg-white" : ""}>
      <Layout.Header
        sidebarExpandedUiPreferenceKey="profileSidebarExpanded"
        title={
          <div className="flex items-center gap-4">
            {profile ? (
              <Button
                size={ButtonSize.medium}
                theme={buttonTheme.text}
                to={getUrlUsingTypename(profile)}
              >
                <Avatar user={profile} className="h-4 w-4 mr-1" />
                {profile.name}
              </Button>
            ) : (
              <span>Profile</span>
            )}
            {tabButtons.length > 0 && (
              <ToggleButtonGroup<string>
                buttons={tabButtons}
                onClick={handleTabClick}
                theme={ToggleButtonGroupTheme.separatedWhiteButtons}
              />
            )}
          </div>
        }
      />
      <Layout.Container>
        <Layout.Main
          width="md"
          sidebarExpandedUiPreferenceKey="profileSidebarExpanded"
        >
          {loading ? (
            <Loading>Loading profile...</Loading>
          ) : !profile ? (
            <div>
              <ProfileEmptyState>Profile not found.</ProfileEmptyState>
            </div>
          ) : (
            <Switch>
              <Route exact path={path}>
                <ProfileOverview profileId={profile.id} />
              </Route>
              <Route exact path={`${path}/work-history`}>
                <ProfileWorkHistory profileId={profile.id} />
              </Route>
              {canViewCareerTab && (
                <Route exact path={`${path}/career`}>
                  <ProfileCareer profileId={profile.id} />
                </Route>
              )}
              {showCoachingTab && (
                <Route exact path={`${path}/coaching`}>
                  <CoachingPage
                    profileId={profile.id}
                    managerId={coachingManagerId}
                  />
                </Route>
              )}
            </Switch>
          )}
        </Layout.Main>
        {profile ? (
          <ProfilePageSidebar
            profileId={profile.id}
            showPrivateNotes={!!showPrivateNotes}
            subjectName={profile.name}
          />
        ) : (
          <Layout.Sidebar sidebarExpandedUiPreferenceKey="profileSidebarExpanded">
            <div />
          </Layout.Sidebar>
        )}
      </Layout.Container>
    </Layout>
  );
};

export default ProfilePage;
